import React, { useEffect, useState, useCallback } from "react";
import Container from "react-bootstrap/Container";
import Spinner from "react-bootstrap/Spinner";
import Table from "react-bootstrap/Table";
import Modal from "react-bootstrap/Modal";
import Swal from "sweetalert2";
import moment from "moment";
import "moment/locale/es";
import { Link } from "react-router-dom";
import { AiOutlineTeam, AiOutlineDown, AiOutlineRight, AiOutlineHistory, AiOutlineFileText, AiOutlineBulb, AiOutlineRead, AiOutlineLineChart } from "react-icons/ai";
import Header from "../../components/protected/Header";
import Footer from "../../components/footer";
import { auth } from "../../config/firebase";
import { useTranslations } from "../../translations";
import { useSchoolRole } from "../../hooks/useSchoolRole";
import { StudentActivityModal } from "../../components/protected/activity/StudentActivity";

const API_BASE = (
  process.env.REACT_APP_BLOOMS_API_URL || "https://ai-blooms.vercel.app/api/openai/blooms"
).replace(/\/api\/.*/, "");

const PER_PAGE = 8;
const ACTIVE_DAYS = 7; // "activo" = ingresó en los últimos 7 días

// ── Monograma (avatar con iniciales) ──
const MONO = [
  ["#eb2178", "#ff7bb0"], ["#632d43", "#a24d6f"], ["#2e7d6b", "#5bc0aa"],
  ["#3b5bdb", "#7d97ff"], ["#d9770a", "#ffb64d"], ["#7048e8", "#a487ff"],
];
const initials = (name = "") => {
  const w = String(name).trim().split(/\s+/).filter(Boolean);
  if (!w.length) return "—";
  return ((w[0][0] || "") + (w[1]?.[0] || w[0][1] || "")).toUpperCase();
};
const hashIdx = (s = "") => {
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return h % MONO.length;
};
const Monogram = ({ name }) => {
  const [a, b] = MONO[hashIdx(name || "")];
  return (
    <span className="tp-mono-av" style={{ background: `linear-gradient(135deg, ${a}, ${b})` }}>
      {initials(name)}
    </span>
  );
};

/** Estado de actividad a partir del último acceso (unix seg). */
const activityOf = (lastAccessTs) => {
  if (!lastAccessTs) return { key: "never", days: null };
  const days = Math.floor((Date.now() / 1000 - lastAccessTs) / 86400);
  return { key: days <= ACTIVE_DAYS ? "active" : "inactive", days };
};

const Pager = ({ t, page, total, perPage, onPage }) => {
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  if (totalPages <= 1) return null;
  return (
    <div className="tp-pager">
      <button type="button" className="tp-page-btn" disabled={page <= 1} onClick={() => onPage(page - 1)}>‹</button>
      <span className="tp-page-info">{t("pageWord")} {page} / {totalPages}</span>
      <button type="button" className="tp-page-btn" disabled={page >= totalPages} onClick={() => onPage(page + 1)}>›</button>
    </div>
  );
};

const TeacherPanel = () => {
  const { t } = useTranslations();
  const { role, loading: roleLoading } = useSchoolRole();
  const isTeacher = role === "maestro";
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(null);
  const [studentsBySection, setStudentsBySection] = useState({}); // { sectionId: [...] | "loading" }
  const [pageBySection, setPageBySection] = useState({});

  moment.locale("es");

  const authHeader = useCallback(async () => {
    const idToken = auth.currentUser ? await auth.currentUser.getIdToken(true) : null;
    return { Authorization: `Bearer ${idToken}` };
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/school/sections`, { headers: await authHeader() });
      const data = await res.json();
      setSections(data.sections || []);
    } catch (e) {
      /* noop */
    } finally {
      setLoading(false);
    }
  }, [authHeader]);

  const loadStudents = useCallback(async (sectionId) => {
    setStudentsBySection((p) => ({ ...p, [sectionId]: "loading" }));
    try {
      const res = await fetch(`${API_BASE}/api/school/students?sectionId=${sectionId}`, { headers: await authHeader() });
      const data = await res.json();
      setStudentsBySection((p) => ({ ...p, [sectionId]: data.students || [] }));
    } catch (e) {
      setStudentsBySection((p) => ({ ...p, [sectionId]: [] }));
    }
  }, [authHeader]);

  const toggleSection = (sectionId) => {
    if (expanded === sectionId) { setExpanded(null); return; }
    setExpanded(sectionId);
    if (!studentsBySection[sectionId]) loadStudents(sectionId);
  };

  useEffect(() => {
    if (!roleLoading && isTeacher) load();
  }, [roleLoading, isTeacher, load]);

  const setSecPage = (id, p) => setPageBySection((prev) => ({ ...prev, [id]: p }));

  // ── Palabras del alumno (Agregadas / Familia) ──
  const [wordsStu, setWordsStu] = useState(null);
  const [actStu, setActStu] = useState(null);
  const [wordsData, setWordsData] = useState(null); // array | "loading"
  const [wordsTab, setWordsTab] = useState("added"); // added | family
  const [wPage, setWPage] = useState(1);

  const openWords = async (s) => {
    setWordsStu(s); setWordsData("loading"); setWordsTab("added"); setWPage(1);
    try {
      const res = await fetch(`${API_BASE}/api/school/student-words?uid=${s.uid}`, { headers: await authHeader() });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Error");
      setWordsData(Array.isArray(data.words) ? data.words : []);
    } catch (e) {
      setWordsData([]);
      Swal.fire({ icon: "error", title: t("errorTitle") || "Error", text: e.message });
    }
  };
  const changeWordsTab = (tab) => { setWordsTab(tab); setWPage(1); };
  const showWordMedia = (w) => {
    // La imagen se precarga: si el archivo ya no existe en Storage (404), no se muestra
    // rota — abre el modal solo con el audio y los datos.
    const fire = (imageUrl) =>
      Swal.fire({
        title: w.spanishName || "",
        html: w.spanishSound ? `<audio src="${w.spanishSound}" controls type="audio/mp4"/>` : "",
        imageUrl: imageUrl || undefined,
        imageAlt: w.spanishName || "",
        confirmButtonColor: "#eb2178",
      });
    if (w.imagePath) {
      const img = new Image();
      img.onload = () => fire(w.imagePath);
      img.onerror = () => fire(undefined);
      img.src = w.imagePath;
    } else fire(undefined);
  };
  const filterWords = (arr, tab) =>
    (Array.isArray(arr) ? arr : []).filter((w) =>
      tab === "family"
        ? (w.type !== "" && w.type !== null && w.type !== undefined)
        : ((w.isCreated === 1 || w.isCreated || w.isCreated === "true") && !w.type)
    );

  const renderStatus = (lastAccessTs) => {
    const { key, days } = activityOf(lastAccessTs);
    if (key === "never") return <span className="tp-chip tp-chip-never">{t("stNever")}</span>;
    if (key === "active") return <span className="tp-chip tp-chip-active">{t("stActive")}</span>;
    return <span className="tp-chip tp-chip-inactive">{t("stInactive")} · {days}{t("daysShort")}</span>;
  };

  return (
    <div className="dashboard-wrapper">
      <Header />
      <main className="dashboard-main-content pb-5">
        <Container fluid className="py-4 mx-auto tp-wrap">
          <div className="tp-head">
            <div className="tp-head-ic"><AiOutlineTeam /></div>
            <div>
              <div className="tp-eyebrow">{t("teacherEyebrow")}</div>
              <h2 className="tp-title">{t("teacherPanelTitle")}</h2>
            </div>
          </div>
          <p className="tp-desc">{t("teacherPanelDesc")}</p>

          {roleLoading || loading ? (
            <div className="text-center py-5"><Spinner animation="border" style={{ color: "var(--tp-plum)" }} /></div>
          ) : !isTeacher ? (
            <div className="tp-card text-center text-muted py-4">{t("notAuthorized")}</div>
          ) : sections.length === 0 ? (
            <div className="tp-card"><div className="tp-empty"><div className="tp-empty-ic">🧩</div><div className="tp-empty-t">{t("noSectionsTeacher")}</div></div></div>
          ) : (
            sections.map((sec) => {
              const list = studentsBySection[sec.id];
              const isOpen = expanded === sec.id;
              const arr = Array.isArray(list) ? list : [];
              const active = arr.filter((s) => activityOf(s.lastAccessTs).key === "active").length;
              const conns = arr.reduce((n, s) => n + (s.connCount || 0), 0);
              const page = pageBySection[sec.id] || 1;
              const totalPages = Math.max(1, Math.ceil(arr.length / PER_PAGE));
              const p = Math.min(page, totalPages);
              const pageItems = arr.slice((p - 1) * PER_PAGE, p * PER_PAGE);
              return (
                <div key={sec.id} className="tp-card tp-section">
                  <div className="tp-sec-head" onClick={() => toggleSection(sec.id)}>
                    <span className="tp-caret">{isOpen ? <AiOutlineDown /> : <AiOutlineRight />}</span>
                    <span className="tp-sec-ic"><AiOutlineTeam /></span>
                    <div className="flex-grow-1">
                      <div className="tp-sec-name">
                        {sec.name}{sec.grade ? <span className="tp-grade-pill">{sec.grade}</span> : null}
                      </div>
                      <div className="tp-sec-sub">
                        <b>{sec.studentCount ?? (Array.isArray(list) ? list.length : "…")}</b> {t("students").toLowerCase()}
                      </div>
                    </div>
                    {Array.isArray(list) && (
                      <div className="tp-mini-stats d-none d-sm-flex">
                        <div className="tp-mini"><span className="tp-mini-n">{active}</span><span className="tp-mini-l">{t("summaryActive")}</span></div>
                        <div className="tp-mini"><span className="tp-mini-n">{conns}</span><span className="tp-mini-l">{t("summaryConns")}</span></div>
                      </div>
                    )}
                  </div>

                  {isOpen && (
                    <div className="tp-sec-body">
                      {list === "loading" || list === undefined ? (
                        <div className="text-center py-4"><Spinner animation="border" size="sm" style={{ color: "var(--tp-plum)" }} /></div>
                      ) : arr.length === 0 ? (
                        <div className="tp-hint">{t("noStudents")}</div>
                      ) : (
                        <>
                          <div className="tp-tablewrap">
                            <Table className="tp-table mb-0">
                              <thead><tr>
                                <th style={{ width: 30 }}>#</th>
                                <th>{t("studentName")}</th>
                                <th><AiOutlineHistory className="me-1" />{t("colLastAccess")}</th>
                                <th className="text-center">{t("colConnections")}</th>
                                <th className="text-end">{t("colStatus")}</th>
                                <th className="text-center">{t("wordsCol")}</th>
                              </tr></thead>
                              <tbody>
                                {pageItems.map((s, i) => (
                                  <tr key={s.uid}>
                                    <td className="tp-idx">{(p - 1) * PER_PAGE + i + 1}</td>
                                    <td>
                                      <div className="tp-person"><Monogram name={s.learnerName} />
                                        <div>
                                          <div className="tp-person-name">{s.learnerName || "—"}</div>
                                          <div className="tp-sub">{s.email}</div>
                                        </div>
                                      </div>
                                    </td>
                                    <td className="tp-access">{s.lastAccessTs ? moment.unix(s.lastAccessTs).fromNow() : <span className="tp-muted">{t("neverWord")}</span>}</td>
                                    <td className="text-center tp-conn">{s.connCount || 0}</td>
                                    <td className="text-end">{renderStatus(s.lastAccessTs)}</td>
                                    <td className="text-center">
                                      <button type="button" className="tp-words-btn me-1" title={t("actView")} onClick={() => setActStu(s)}>
                                        <AiOutlineLineChart />
                                      </button>
                                      <button type="button" className="tp-words-btn" title={t("viewWords")} onClick={() => openWords(s)}>
                                        <AiOutlineRead />
                                      </button>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </Table>
                          </div>
                          <Pager t={t} page={p} total={arr.length} perPage={PER_PAGE} onPage={(np) => setSecPage(sec.id, np)} />
                        </>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}

          {/* Guía para autorear + repartir (reusa las páginas reales del portal) */}
          {isTeacher && !loading && (
            <div className="tp-author">
              <div className="tp-author-head">{t("teacherAuthorTitle")}</div>
              <p className="tp-author-txt">{t("teacherAuthorBody")}</p>
              <div className="tp-author-btns">
                <Link to="/actions/create-text" className="tp-gen-btn"><AiOutlineFileText /> {t("createText")}</Link>
                <Link to="/inference/create" className="tp-gen-btn tp-gen-alt"><AiOutlineBulb /> {t("generateInference")}</Link>
              </div>
            </div>
          )}
        </Container>
        <Footer logged={true} />
      </main>

      {/* Modal: palabras del alumno (Agregadas / Familia) */}
      <StudentActivityModal student={actStu} onHide={() => setActStu(null)} />

      <Modal show={!!wordsStu} onHide={() => setWordsStu(null)} centered size="lg" scrollable>
        <Modal.Header closeButton>
          <Modal.Title className="fs-5 d-flex align-items-center gap-2">
            <span className="tp-sec-ic"><AiOutlineRead /></span>
            {t("studentWordsTitle")} {wordsStu?.learnerName}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="tp-wtabs">
            <button type="button" className={`tp-wtab ${wordsTab === "added" ? "on" : ""}`} onClick={() => changeWordsTab("added")}>{t("added")}</button>
            <button type="button" className={`tp-wtab ${wordsTab === "family" ? "on" : ""}`} onClick={() => changeWordsTab("family")}>{t("family")}</button>
          </div>
          {wordsData === "loading" ? (
            <div className="text-center py-4"><Spinner animation="border" size="sm" style={{ color: "var(--tp-plum)" }} /></div>
          ) : (() => {
            const all = filterWords(wordsData, wordsTab);
            const WPER = 6;
            const total = all.length;
            const wp = Math.min(wPage, Math.max(1, Math.ceil(total / WPER)));
            const items = all.slice((wp - 1) * WPER, wp * WPER);
            if (!total) return <div className="tp-hint">{t("noWords")}</div>;
            return (
              <>
                <div className="tp-tablewrap">
                  <Table className="tp-table mb-0">
                    <thead><tr>
                      <th style={{ width: 30 }}>#</th>
                      <th>{t("spanish")}</th>
                      <th>{t("syllables")}</th>
                      <th>{t("english")}</th>
                      {wordsTab !== "family" && <th>{t("topic")}</th>}
                      <th className="text-center">{t("details")}</th>
                    </tr></thead>
                    <tbody>
                      {items.map((w, i) => (
                        <tr key={i}>
                          <td className="tp-idx">{(wp - 1) * WPER + i + 1}</td>
                          <td className="tp-person-name">{w.spanishName}</td>
                          <td><span className="tp-syl">{w.spanishBreakUp}</span></td>
                          <td className="tp-sub" style={{ maxWidth: "none" }}>{w.englishName}</td>
                          {wordsTab !== "family" && <td className="tp-sub" style={{ maxWidth: "none" }}>{w.tema}</td>}
                          <td className="text-center"><button type="button" className="tp-ver-btn" onClick={() => showWordMedia(w)}>{t("view")}</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                </div>
                <Pager t={t} page={wp} total={total} perPage={WPER} onPage={setWPage} />
              </>
            );
          })()}
        </Modal.Body>
      </Modal>

      <style>{`
        :root { --tp-plum:#632d43; --tp-pink:#eb2178; --tp-ink:#241f2b; --tp-muted:#8b8593; --tp-line:#eceaf0; }
        .tp-wrap { max-width: 980px; }
        .tp-head { display:flex; align-items:center; gap:14px; }
        .tp-head-ic { width:46px; height:46px; border-radius:14px; display:flex; align-items:center; justify-content:center;
          font-size:1.4rem; color:#fff; background:linear-gradient(135deg,var(--tp-plum),var(--tp-pink)); flex-shrink:0; box-shadow:0 8px 18px rgba(99,45,67,.25); }
        .tp-eyebrow { font-size:.72rem; text-transform:uppercase; letter-spacing:1.2px; color:var(--tp-muted); font-weight:700; }
        .tp-title { font-size:1.7rem; font-weight:800; color:var(--tp-ink); margin:0; letter-spacing:-.5px; }
        .tp-desc { color:var(--tp-muted); margin:10px 0 22px; font-size:.95rem; max-width:640px; }
        .tp-card { background:#fff; border:1px solid var(--tp-line); border-radius:18px; box-shadow:0 4px 24px rgba(36,31,43,.05); margin-bottom:14px; }
        .tp-section { overflow:hidden; }
        .tp-sec-head { display:flex; align-items:center; gap:12px; padding:16px 20px; cursor:pointer; transition:background .15s; }
        .tp-sec-head:hover { background:#fcfafb; }
        .tp-caret { color:var(--tp-muted); display:inline-flex; font-size:.9rem; }
        .tp-sec-ic { width:38px; height:38px; border-radius:11px; display:inline-flex; align-items:center; justify-content:center; font-size:1.1rem; background:#e7f6f2; color:#2e7d6b; flex-shrink:0; }
        .tp-sec-name { font-weight:700; color:var(--tp-ink); font-size:1.02rem; display:flex; align-items:center; gap:9px; flex-wrap:wrap; }
        .tp-grade-pill { display:inline-block; padding:2px 10px; border-radius:999px; font-size:.74rem; font-weight:700; background:#f0e9f7; color:#6a3ec9; }
        .tp-sec-sub { color:var(--tp-muted); font-size:.85rem; margin-top:2px; }
        .tp-mini-stats { gap:22px; padding-right:6px; }
        .tp-mini { text-align:center; display:flex; flex-direction:column; }
        .tp-mini-n { font-size:1.25rem; font-weight:800; color:var(--tp-plum); line-height:1; }
        .tp-mini-l { font-size:.66rem; text-transform:uppercase; letter-spacing:.5px; color:var(--tp-muted); font-weight:700; margin-top:3px; }
        .tp-sec-body { padding:4px 20px 18px; border-top:1px solid var(--tp-line); }
        .tp-tablewrap { overflow-x:auto; }
        .tp-table { width:100%; min-width:520px; border-collapse:separate; border-spacing:0; margin:0; }
        .tp-table thead th { font-size:.68rem; text-transform:uppercase; letter-spacing:.6px; color:var(--tp-muted); font-weight:800; border-bottom:2px solid var(--tp-line); padding:10px 10px; white-space:nowrap; }
        .tp-table tbody td { padding:11px 10px; border-bottom:1px solid #f4f2f6; vertical-align:middle; font-size:.9rem; color:var(--tp-ink); }
        .tp-table tbody tr:last-child td { border-bottom:none; }
        .tp-table tbody tr:hover td { background:#fcfafb; }
        .tp-idx { color:#c3bcc9; font-weight:700; font-size:.82rem; }
        .tp-person { display:flex; align-items:center; gap:11px; }
        .tp-person-name { font-weight:600; color:var(--tp-ink); }
        .tp-sub { color:var(--tp-muted); font-size:.78rem; margin-top:1px; max-width:200px; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }
        .tp-mono-av { width:34px; height:34px; border-radius:50%; display:inline-flex; align-items:center; justify-content:center; color:#fff; font-weight:800; font-size:.78rem; flex-shrink:0; }
        .tp-access { color:var(--tp-ink); font-size:.86rem; white-space:nowrap; }
        .tp-muted { color:var(--tp-muted); }
        .tp-conn { font-weight:800; color:var(--tp-plum); }
        .tp-chip { display:inline-block; padding:3px 11px; border-radius:999px; font-size:.74rem; font-weight:700; white-space:nowrap; }
        .tp-chip-active { background:#e7f7ee; color:#1b7a43; }
        .tp-chip-inactive { background:#fff6e5; color:#a86a00; }
        .tp-chip-never { background:#f3f2f5; color:#9a94a3; }
        .tp-hint { color:var(--tp-muted); font-size:.88rem; padding:14px 2px; }
        .tp-pager { display:flex; align-items:center; justify-content:center; gap:16px; margin-top:14px; }
        .tp-page-btn { width:34px; height:34px; border-radius:10px; border:1.5px solid var(--tp-line); background:#fff; font-size:1.2rem; color:var(--tp-plum); display:inline-flex; align-items:center; justify-content:center; cursor:pointer; }
        .tp-page-btn:disabled { opacity:.35; cursor:default; }
        .tp-page-info { font-size:.84rem; color:var(--tp-muted); font-weight:700; }
        .tp-empty { text-align:center; padding:34px 20px; }
        .tp-empty-ic { font-size:2.2rem; margin-bottom:8px; }
        .tp-empty-t { font-weight:700; color:var(--tp-ink); }
        /* Palabras */
        .tp-words-btn { width:32px; height:32px; border-radius:9px; border:1px solid var(--tp-line); background:#fff; color:var(--tp-plum); display:inline-flex; align-items:center; justify-content:center; cursor:pointer; font-size:1rem; transition:all .15s; }
        .tp-words-btn:hover { background:#f7f0fb; border-color:#e2d3ec; color:var(--tp-pink); }
        .tp-wtabs { display:flex; gap:8px; margin-bottom:14px; }
        .tp-wtab { border:1.5px solid var(--tp-line); background:#fff; color:var(--tp-muted); border-radius:999px; padding:6px 18px; font-size:.85rem; font-weight:700; cursor:pointer; transition:all .15s; }
        .tp-wtab.on { background:linear-gradient(135deg,var(--tp-plum),var(--tp-pink)); color:#fff; border-color:transparent; }
        .tp-syl { display:inline-block; padding:2px 10px; border-radius:999px; background:#f3f2f5; color:#6b6572; font-size:.8rem; font-weight:600; }
        .tp-ver-btn { border:none; background:#f4eef1; color:var(--tp-plum); border-radius:9px; padding:5px 14px; font-size:.82rem; font-weight:700; cursor:pointer; }
        .tp-ver-btn:hover { background:#ece3e8; }
        /* Autor: generar contenido (reusa las páginas del portal) + repartir a la sección */
        .tp-author { margin-top:18px; background:linear-gradient(135deg,#fbf5f8,#f7f0fb); border:1px solid #f0dce7; border-radius:18px; padding:22px 24px; }
        .tp-author-head { font-size:1.05rem; font-weight:800; color:var(--tp-ink); }
        .tp-author-txt { color:var(--tp-muted); font-size:.9rem; margin:6px 0 16px; max-width:640px; }
        .tp-author-btns { display:flex; gap:10px; flex-wrap:wrap; }
        .tp-gen-btn { display:inline-flex; align-items:center; gap:8px; text-decoration:none; background:linear-gradient(135deg,var(--tp-plum),var(--tp-pink)); color:#fff; border:none; border-radius:12px; padding:11px 20px; font-size:.9rem; font-weight:700; cursor:pointer; box-shadow:0 6px 16px rgba(235,33,120,.22); transition:all .18s; }
        .tp-gen-btn:hover { filter:brightness(1.05); transform:translateY(-1px); color:#fff; }
        .tp-gen-alt { background:linear-gradient(135deg,#7048e8,#a487ff); box-shadow:0 6px 16px rgba(112,72,232,.22); }
        @media (max-width:576px){ .tp-title { font-size:1.4rem; } .tp-sec-head { padding:14px 16px; } .tp-sec-body { padding:4px 12px 14px; } }
      `}</style>
    </div>
  );
};

export default TeacherPanel;
